import _, { initial } from "lodash";
import { type UnknownAction } from "@reduxjs/toolkit";
import {
  FetchTeamsDashboardErrorResponse,
  FetchTeamsDashboardResponse,
  FetchTeamsLeaderboardErrorResponse,
  FetchTeamsLeaderboardRequest,
  FetchTeamsLeaderboardResponse,
  FetchTeamsLearnersErrorResponse,
  FetchTeamsLearnersRequest,
  FetchTeamsLearnersResponse,
  FetchTeamsLearningsErrorResponse,
  FetchTeamsLearningsRequest,
  FetchTeamsLearningsResponse,
  FetchTeamsMostEnrolledLearningsErrorResponse,
  FetchTeamsMostEnrolledLearningsRequest,
  FetchTeamsMostEnrolledLearningsResponse,
  FetchTeamsSkillsErrorResponse,
  FetchTeamsSkillsRequest,
  FetchTeamsSkillsResponse,
  TeamsDashboard,
  TeamsLearners,
  TeamsLearning,
  TeamsSkills,
} from "src/types/coaching/reports/teams";
import {
  FETCH_CREATIONS_DASHBOARD_REPORT_REQUEST,
  FETCH_CREATIONS_LEARNERS_REPORT_REQUEST,
  FETCH_CREATIONS_LEARNINGS_INSIGHTS_REPORT_REQUEST,
  FETCH_CREATIONS_TOP_LEARNINGS_REPORT_REQUEST,
  FETCH_LEARNINGS_DASHBOARD_REPORT_REQUEST,
  FETCH_LEARNINGS_INSIGHTS_REPORT_REQUEST,
  FETCH_LEARNINGS_LEADERBOARD_REPORT_REQUEST,
  FETCH_LEARNINGS_POPULAR_REPORT_REQUEST,
  FETCH_LEARNINGS_SKILLS_REPORT_REQUEST,
  FETCH_SKILLS_REPORT_REQUEST,
  FETCH_TEAMS_DASHBOARD_REPORT_REQUEST,
  FETCH_TEAMS_LEADERBOARD_REPORT_REQUEST,
  FETCH_TEAMS_LEARNERS_REPORT_REQUEST,
  FETCH_TEAMS_LEARNINGS_REPORT_REQUEST,
  FETCH_TEAMS_MOST_ENROLLED_LEARNINGS_REPORT_REQUEST,
  FETCHING_CREATIONS_DASHBOARD_REPORT_FAILED,
  FETCHING_CREATIONS_DASHBOARD_REPORT_SUCCEEDED,
  FETCHING_CREATIONS_LEARNERS_REPORT_FAILED,
  FETCHING_CREATIONS_LEARNERS_REPORT_SUCCEEDED,
  FETCHING_CREATIONS_LEARNINGS_INSIGHTS_REPORT_FAILED,
  FETCHING_CREATIONS_LEARNINGS_INSIGHTS_REPORT_SUCCEEDED,
  FETCHING_CREATIONS_TOP_LEARNINGS_REPORT_FAILED,
  FETCHING_CREATIONS_TOP_LEARNINGS_REPORT_SUCCEEDED,
  FETCHING_LEARNINGS_DASHBOARD_REPORT_FAILED,
  FETCHING_LEARNINGS_DASHBOARD_REPORT_SUCCEEDED,
  FETCHING_LEARNINGS_INSIGHTS_REPORT_FAILED,
  FETCHING_LEARNINGS_INSIGHTS_REPORT_SUCCEEDED,
  FETCHING_LEARNINGS_LEADERBOARD_REPORT_FAILED,
  FETCHING_LEARNINGS_LEADERBOARD_REPORT_SUCCEEDED,
  FETCHING_LEARNINGS_POPULAR_REPORT_FAILED,
  FETCHING_LEARNINGS_POPULAR_REPORT_SUCCEEDED,
  FETCHING_LEARNINGS_SKILLS_REPORT_FAILED,
  FETCHING_LEARNINGS_SKILLS_REPORT_SUCCEEDED,
  FETCHING_SKILLS_FAILED,
  FETCHING_SKILLS_REPORT_SUCCEEDED,
  FETCHING_TEAMS_DASHBOARD_REPORT_FAILED,
  FETCHING_TEAMS_DASHBOARD_REPORT_SUCCEEDED,
  FETCHING_TEAMS_LEADERBOARD_REPORT_FAILED,
  FETCHING_TEAMS_LEADERBOARD_REPORT_SUCCEEDED,
  FETCHING_TEAMS_LEARNERS_REPORT_SUCCEEDED,
  FETCHING_TEAMS_LEARNINGS_REPORT_FAILED,
  FETCHING_TEAMS_LEARNINGS_REPORT_SUCCEEDED,
  FETCHING_TEAMS_MOST_ENROLLED_LEARNINGS_REPORT_FAILED,
  FETCHING_TEAMS_MOST_ENROLLED_LEARNINGS_REPORT_SUCCEEDED,
} from "src/redux/actions/actionTypes";
import { init } from "ace-builds/src-noconflict/ext-keybinding_menu";

interface ReportsState {
  teams: TeamsReportState;
  learnings: LearningsReportState;
  creations: CreationsReportState;
}

interface TeamsReportState {
  learnings: EntityState<TeamsLearning>;
  mostEnrolledLearnings: TeamsMostEnrolledLearnings;
  learners: EntityState<TeamsLearners>;
  leaderboard: TeamsLeaderboardState;
  dashboard: TeamsReportDashboardState;
  skills: EntityState<TeamsSkills>;
}

interface LearningsReportState {
  insights: EntityState<TeamsLearning>;
  mostEnrolledLearnings: TeamsMostEnrolledLearnings;
  learners: EntityState<TeamsLearners>;
  leaderboard: TeamsLeaderboardState;
  dashboard: TeamsReportDashboardState;
  skills: EntityState<TeamsSkills>;
}

interface CreationsReportState {
  learnings: EntityState<TeamsLearning>;
  mostEnrolledLearnings: TeamsMostEnrolledLearnings;
  learners: EntityState<TeamsLearners>;
  leaderboard: TeamsLeaderboardState;
  dashboard: TeamsReportDashboardState;
}

interface EntityState<T> {
  loading: boolean;
  loaded: boolean;
  error: any;
  items: T[];
  pagination: {
    currentPage: number;
    totalPages: number;
    recordsPerPage: number;
    totalRecordsCount: number;
    hasMoreItems: boolean;
  };
}

interface TeamsLearningState {
  loading: boolean;
  loaded: boolean;
  error: any;
  items: TeamsLearning[];
  pagination: {
    currentPage: number;
    totalPages: number;
    recordsPerPage: number;
    totalRecordsCount: number;
    hasMoreItems: boolean;
  };
}

interface TeamsMostEnrolledLearnings {
  loading: boolean;
  loaded: boolean;
  error: any;
  items: TeamsLearning[];
}

interface TeamsLearnerState {
  loading: boolean;
  loaded: boolean;
  error: any;
  items: TeamsLearners[];
  pagination: {
    currentPage: number;
    totalPages: number;
    recordsPerPage: number;
    totalRecordsCount: number;
    hasMoreItems: boolean;
  };
}

interface TeamsLeaderboardState {
  loading: boolean;
  loaded: boolean;
  error: any;
  userRank: TeamsLearners;
  leaders: TeamsLearners[];
}

interface TeamsReportDashboardState {
  loading: boolean;
  loaded: boolean;
  error: any;
  item: TeamsDashboard;
}

const initialTeamsLearningState: TeamsLearningState = {
  loading: false,
  loaded: false,
  error: null,
  items: [],
  pagination: {
    currentPage: 0,
    totalPages: 0,
    recordsPerPage: 0,
    totalRecordsCount: 0,
    hasMoreItems: false,
  },
};

const initialTeamsMostEnrolledLearningsState: TeamsMostEnrolledLearnings = {
  loading: false,
  loaded: false,
  error: null,
  items: [],
};

const initialTeamsLearnersState: TeamsLearnerState = {
  loading: false,
  loaded: false,
  error: null,
  items: [],
  pagination: {
    currentPage: 0,
    totalPages: 0,
    recordsPerPage: 0,
    totalRecordsCount: 0,
    hasMoreItems: false,
  },
};

const initialTeamsLeaderboardState: TeamsLeaderboardState = {
  loading: false,
  loaded: false,
  error: null,
  userRank: {} as TeamsLearners,
  leaders: [] as TeamsLearners[],
};

const initialTeamsDashboardState: TeamsReportDashboardState = {
  loading: false,
  loaded: false,
  error: null,
  item: {} as TeamsDashboard,
};

const initialTeamsSkillsState: EntityState<TeamsSkills> = {
  loading: false,
  loaded: false,
  error: null,
  items: [],
  pagination: {
    currentPage: 0,
    totalPages: 0,
    recordsPerPage: 0,
    totalRecordsCount: 0,
    hasMoreItems: false,
  },
};

const initialState: ReportsState = {
  teams: {
    learnings: initialTeamsLearningState,
    mostEnrolledLearnings: initialTeamsMostEnrolledLearningsState,
    learners: initialTeamsLearnersState,
    leaderboard: initialTeamsLeaderboardState,
    dashboard: initialTeamsDashboardState,
    skills: initialTeamsSkillsState,
  },
  learnings: {
    insights: initialTeamsLearningState,
    mostEnrolledLearnings: initialTeamsMostEnrolledLearningsState,
    learners: initialTeamsLearnersState,
    leaderboard: initialTeamsLeaderboardState,
    dashboard: initialTeamsDashboardState,
    skills: initialTeamsSkillsState,
  },
  creations: {
    learnings: initialTeamsLearningState,
    mostEnrolledLearnings: initialTeamsMostEnrolledLearningsState,
    learners: initialTeamsLearnersState,
    leaderboard: initialTeamsLeaderboardState,
    dashboard: initialTeamsDashboardState,
  },
};

export function reportsReduder(
  state: ReportsState = initialState,
  action: UnknownAction,
): ReportsState {
  switch (action.type) {
    case FETCH_TEAMS_LEARNINGS_REPORT_REQUEST: {
      const { page, limit, startDate, endDate } =
        action.payload as FetchTeamsLearningsRequest;

      let existingItems: any[] = state.teams.learnings.items || [];
      if (page === 0) {
        existingItems = [];
      }

      return {
        ...state,
        teams: {
          ...state.teams,
          learnings: {
            ...state.teams.learnings,
            loading: true,
            loaded: false,
            error: null,
            items: existingItems,
          },
        },
      };
    }

    case FETCHING_TEAMS_LEARNINGS_REPORT_SUCCEEDED: {
      const { response } = action.payload as FetchTeamsLearningsResponse;
      const existingItems: any[] = state.teams.learnings.items || [];
      let records = response.records;

      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      return {
        ...state,
        teams: {
          ...state.teams,
          learnings: {
            ...state.teams.learnings,
            loading: false,
            loaded: true,
            items: records,
            pagination: {
              currentPage: response.currentPage,
              totalPages: response.totalPages,
              recordsPerPage: response.recordsPerPage,
              totalRecordsCount: response.totalRecordsCount,
              hasMoreItems: response.currentPage < response.totalPages - 1,
            },
          },
        },
      };
    }

    case FETCHING_TEAMS_LEARNINGS_REPORT_FAILED: {
      const { error } = action.payload as FetchTeamsLearningsErrorResponse;
      return {
        ...state,
        teams: {
          ...state.teams,
          learnings: {
            ...state.teams.learnings,
            loading: false,
            loaded: false,
            error: error,
          },
        },
      };
    }

    case FETCH_TEAMS_LEARNERS_REPORT_REQUEST: {
      const { page, limit, startDate, endDate } =
        action.payload as FetchTeamsLearnersRequest;

      let existingItems: any[] = state.teams.learners.items || [];
      if (page === 0) {
        existingItems = [];
      }

      return {
        ...state,
        teams: {
          ...state.teams,
          learners: {
            ...state.teams.learners,
            loading: true,
            loaded: false,
            error: null,
          },
        },
      };
    }

    case FETCHING_TEAMS_LEARNERS_REPORT_SUCCEEDED: {
      const { response } = action.payload as FetchTeamsLearnersResponse;
      const existingItems: any[] = state.teams.learners.items || [];
      let records = response.records;

      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      records = _.uniqBy(records, "user.id"); 

      return {
        ...state,
        teams: {
          ...state.teams,
          learners: {
            ...state.teams.learners,
            loading: false,
            loaded: true,
            items: records,
            pagination: {
              currentPage: response.currentPage,
              totalPages: response.totalPages,
              recordsPerPage: response.recordsPerPage,
              totalRecordsCount: response.totalRecordsCount,
              hasMoreItems: response.currentPage < response.totalPages - 1,
            },
          },
        },
      };
    }

    case FETCHING_TEAMS_LEARNINGS_REPORT_FAILED: {
      const { error } = action.payload as FetchTeamsLearnersErrorResponse;
      return {
        ...state,
        teams: {
          ...state.teams,
          learners: {
            ...state.teams.learners,
            loading: false,
            loaded: false,
            error: error,
          },
        },
      };
    }

    case FETCH_TEAMS_LEADERBOARD_REPORT_REQUEST: {
      return {
        ...state,
        teams: {
          ...state.teams,
          leaderboard: {
            ...state.teams.leaderboard,
            loading: true,
            loaded: false,
            error: null,
          },
        },
      };
    }

    case FETCHING_TEAMS_LEADERBOARD_REPORT_SUCCEEDED: {
      const { response } = action.payload as FetchTeamsLeaderboardResponse;

      const {userRank, leaders} = response;

      return {
        ...state,
        teams: {
          ...state.teams,
          leaderboard: {
            ...state.teams.leaderboard,
            loading: false,
            loaded: true,
            userRank: userRank,
            leaders: leaders,
          },
        },
      };
    }

    case FETCHING_TEAMS_LEADERBOARD_REPORT_FAILED: {
      const { error } = action.payload as FetchTeamsLeaderboardErrorResponse;
      return {
        ...state,
        teams: {
          ...state.teams,
          leaderboard: {
            ...state.teams.leaderboard,
            loading: false,
            loaded: false,
            error: error,
          },
        },
      };
    }

    case FETCHING_TEAMS_LEARNINGS_REPORT_FAILED: {
      const { error } = action.payload as FetchTeamsLearnersErrorResponse;
      return {
        ...state,
        teams: {
          ...state.teams,
          leaderboard: {
            ...state.teams.leaderboard,
            loading: false,
            loaded: false,
            error: error,
          },
        },
      };
    }

    case FETCH_TEAMS_DASHBOARD_REPORT_REQUEST: {
      return {
        ...state,
        teams: {
          ...state.teams,
          dashboard: {
            ...state.teams.dashboard,
            loading: true,
            loaded: false,
            error: null,
          },
        },
      };
    }

    case FETCHING_TEAMS_DASHBOARD_REPORT_SUCCEEDED: {
      const { response } = action.payload as FetchTeamsDashboardResponse;
      return {
        ...state,
        teams: {
          ...state.teams,
          dashboard: {
            ...state.teams.dashboard,
            loading: false,
            loaded: true,
            item: response,
          },
        },
      };
    }

    case FETCHING_TEAMS_DASHBOARD_REPORT_FAILED: {
      const { error } = action.payload as FetchTeamsDashboardErrorResponse;
      return {
        ...state,
        teams: {
          ...state.teams,
          dashboard: {
            ...state.teams.dashboard,
            loading: false,
            loaded: false,
            error: error,
          },
        },
      };
    }

    case FETCH_TEAMS_MOST_ENROLLED_LEARNINGS_REPORT_REQUEST: {
      const { startDate, endDate } =
        action.payload as FetchTeamsMostEnrolledLearningsRequest;

      return {
        ...state,
        teams: {
          ...state.teams,
          mostEnrolledLearnings: {
            ...state.teams.mostEnrolledLearnings,
            loading: true,
            loaded: false,
            error: null,
          },
        },
      };
    }

    case FETCHING_TEAMS_MOST_ENROLLED_LEARNINGS_REPORT_SUCCEEDED: {
      const { response } =
        action.payload as FetchTeamsMostEnrolledLearningsResponse;
      return {
        ...state,
        teams: {
          ...state.teams,
          mostEnrolledLearnings: {
            ...state.teams.mostEnrolledLearnings,
            loading: false,
            loaded: true,
            items: response,
          },
        },
      };
    }

    case FETCHING_TEAMS_MOST_ENROLLED_LEARNINGS_REPORT_FAILED: {
      const { error } = action.payload as FetchTeamsMostEnrolledLearningsErrorResponse;
      return {
        ...state,
        teams: {
          ...state.teams,
          mostEnrolledLearnings: {
            ...state.teams.mostEnrolledLearnings,
            loading: false,
            loaded: false,
            error: error,
          },
        },
      };
    }

    case FETCHING_TEAMS_LEARNINGS_REPORT_FAILED: {
      const { error } =
        action.payload as FetchTeamsMostEnrolledLearningsErrorResponse;
      return {
        ...state,
        teams: {
          ...state.teams,
          mostEnrolledLearnings: {
            ...state.teams.mostEnrolledLearnings,
            loading: false,
            loaded: false,
            error: error,
          },
        },
      };
    }

    case FETCH_LEARNINGS_DASHBOARD_REPORT_REQUEST: {
      return {
        ...state,
        learnings: {
          ...state.learnings,
          dashboard: {
            ...state.learnings.dashboard,
            loading: true,
            loaded: false,
            error: null,
          },
        },
      };
    }

    case FETCHING_LEARNINGS_DASHBOARD_REPORT_SUCCEEDED: {
      const { response } = action.payload as FetchTeamsDashboardResponse;
      return {
        ...state,
        learnings: {
          ...state.learnings,
          dashboard: {
            ...state.learnings.dashboard,
            loading: false,
            loaded: true,
            item: response,
          },
        },
      };
    }

    case FETCHING_LEARNINGS_DASHBOARD_REPORT_FAILED: {
      const { error } = action.payload as FetchTeamsDashboardErrorResponse;
      return {
        ...state,
        learnings: {
          ...state.learnings,
          dashboard: {
            ...state.learnings.dashboard,
            loading: false,
            loaded: false,
            error: error,
          },
        },
      };
    }

    case FETCH_LEARNINGS_LEADERBOARD_REPORT_REQUEST: {
      return {
        ...state,
        learnings: {
          ...state.learnings,
          leaderboard: {
            ...state.learnings.leaderboard,
            loading: true,
            loaded: false,
            error: null,
          },
        },
      };
    }

    case FETCHING_LEARNINGS_LEADERBOARD_REPORT_SUCCEEDED: {
      const { response } = action.payload as FetchTeamsLeaderboardResponse;
      const {userRank, leaders} = response;
      return {
        ...state,
        learnings: {
          ...state.learnings,
          leaderboard: {
            ...state.learnings.leaderboard,
            loading: false,
            loaded: true,
            userRank: userRank,
            leaders: leaders
          },
        },
      };
    }

    case FETCHING_LEARNINGS_LEADERBOARD_REPORT_FAILED: {
      const { error } = action.payload as FetchTeamsLeaderboardErrorResponse;
      return {
        ...state,
        learnings: {
          ...state.learnings,
          leaderboard: {
            ...state.learnings.leaderboard,
            loading: false,
            loaded: false,
            error: error,
          },
        },
      };
    }

    case FETCH_LEARNINGS_POPULAR_REPORT_REQUEST: {
      return {
        ...state,
        learnings: {
          ...state.learnings,
          mostEnrolledLearnings: {
            ...state.learnings.mostEnrolledLearnings,
            loading: true,
            loaded: false,
            error: null,
          },
        },
      };
    }

    case FETCHING_LEARNINGS_POPULAR_REPORT_SUCCEEDED: {
      const { response } =
        action.payload as FetchTeamsMostEnrolledLearningsResponse;
      return {
        ...state,
        learnings: {
          ...state.learnings,
          mostEnrolledLearnings: {
            ...state.learnings.mostEnrolledLearnings,
            loading: false,
            loaded: true,
            items: response,
          },
        },
      };
    }

    case FETCHING_LEARNINGS_POPULAR_REPORT_FAILED: {
      const { error } =
        action.payload as FetchTeamsMostEnrolledLearningsErrorResponse;
      return {
        ...state,
        learnings: {
          ...state.learnings,
          mostEnrolledLearnings: {
            ...state.learnings.mostEnrolledLearnings,
            loading: false,
            loaded: false,
            error: error,
          },
        },
      };
    }

    case FETCH_LEARNINGS_INSIGHTS_REPORT_REQUEST: {
      const { page, limit, startDate, endDate } =
        action.payload as FetchTeamsLearningsRequest;

      let existingItems: any[] = state.learnings.insights.items || [];
      if (page === 0) {
        existingItems = [];
      }
      return {
        ...state,
        learnings: {
          ...state.learnings,
          insights: {
            ...state.learnings.insights,
            loading: true,
            loaded: false,
            error: null,
            items: existingItems
          },
        },
      };
    }

    case FETCHING_LEARNINGS_INSIGHTS_REPORT_SUCCEEDED: {
      const { response } = action.payload as FetchTeamsLearningsResponse;
      const existingItems: any[] = state.learnings.insights.items || [];
      let records = response.records;

      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      return {
        ...state,
        learnings: {
          ...state.learnings,
          insights: {
            ...state.learnings.insights,
            loading: false,
            loaded: true,
            items: records,
            pagination: {
              currentPage: response.currentPage,
              totalPages: response.totalPages,
              recordsPerPage: response.recordsPerPage,
              totalRecordsCount: response.totalRecordsCount,
              hasMoreItems: response.currentPage < response.totalPages - 1,
            },
          },
        },
      };
    }

    case FETCHING_LEARNINGS_INSIGHTS_REPORT_FAILED: {
      const { error } = action.payload as FetchTeamsLearningsErrorResponse;
      return {
        ...state,
        learnings: {
          ...state.learnings,
          insights: {
            ...state.learnings.insights,
            loading: false,
            loaded: false,
            error: error,
          },
        },
      };
    }

    case FETCH_CREATIONS_DASHBOARD_REPORT_REQUEST: {
      return {
        ...state,
        creations: {
          ...state.creations,
          dashboard: {
            ...state.creations.dashboard,
            loading: true,
            loaded: false,
            error: null,
          },
        },
      };
    }

    case FETCHING_CREATIONS_DASHBOARD_REPORT_SUCCEEDED: {
      const { response } = action.payload as FetchTeamsDashboardResponse;
      return {
        ...state,
        creations: {
          ...state.creations,
          dashboard: {
            ...state.creations.dashboard,
            loading: false,
            loaded: true,
            item: response,
          },
        },
      };
    }

    case FETCHING_CREATIONS_DASHBOARD_REPORT_FAILED: {
      const { error } = action.payload as FetchTeamsDashboardErrorResponse;
      return {
        ...state,
        creations: {
          ...state.creations,
          dashboard: {
            ...state.creations.dashboard,
            loading: false,
            loaded: false,
            error: error,
          },
        },
      };
    }

    case FETCH_CREATIONS_TOP_LEARNINGS_REPORT_REQUEST: {
      return {
        ...state,
        creations: {
          ...state.creations,
          mostEnrolledLearnings: {
            ...state.creations.mostEnrolledLearnings,
            loading: true,
            loaded: false,
            error: null,
          },
        },
      };
    }

    case FETCHING_CREATIONS_TOP_LEARNINGS_REPORT_SUCCEEDED: {
      const { response } =
        action.payload as FetchTeamsMostEnrolledLearningsResponse;
      return {
        ...state,
        creations: {
          ...state.creations,
          mostEnrolledLearnings: {
            ...state.creations.mostEnrolledLearnings,
            loading: false,
            loaded: true,
            items: response,
          },
        },
      };
    }

    case FETCHING_CREATIONS_TOP_LEARNINGS_REPORT_FAILED: {
      const { error } =
        action.payload as FetchTeamsMostEnrolledLearningsErrorResponse;
      return {
        ...state,
        creations: {
          ...state.creations,
          mostEnrolledLearnings: {
            ...state.creations.mostEnrolledLearnings,
            loading: false,
            loaded: false,
            error: error,
          },
        },
      };
    }

    case FETCH_CREATIONS_LEARNINGS_INSIGHTS_REPORT_REQUEST: {
      const { page, limit, startDate, endDate } =
        action.payload as FetchTeamsLearningsRequest;

      let existingItems: any[] = state.creations.learnings.items || [];
      if (page === 0) {
        existingItems = [];
      }
      return {
        ...state,
        creations: {
          ...state.creations,
          learnings: {
            ...state.creations.learnings,
            loading: true,
            loaded: false,
            error: null,
            items: existingItems
          },
        },
      };
    }

    case FETCHING_CREATIONS_LEARNINGS_INSIGHTS_REPORT_SUCCEEDED: {
      const { response } = action.payload as FetchTeamsLearningsResponse;
      const existingItems: any[] = state.creations.learnings.items || [];
      let records = response.records;

      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      return {
        ...state,
        creations: {
          ...state.creations,
          learnings: {
            ...state.creations.learnings,
            loading: false,
            loaded: true,
            items: records,
            pagination: {
              currentPage: response.currentPage,
              totalPages: response.totalPages,
              recordsPerPage: response.recordsPerPage,
              totalRecordsCount: response.totalRecordsCount,
              hasMoreItems: response.currentPage < response.totalPages - 1,
            },
          },
        },
      };
    }

    case FETCHING_CREATIONS_LEARNINGS_INSIGHTS_REPORT_FAILED: {
      const { error } = action.payload as FetchTeamsLearningsErrorResponse;
      return {
        ...state,
        creations: {
          ...state.creations,
          learnings: {
            ...state.creations.learnings,
            loading: false,
            loaded: false,
            error: error,
          },
        },
      };
    }

    case FETCH_CREATIONS_LEARNERS_REPORT_REQUEST: {
      const { page, limit, startDate, endDate } =
        action.payload as FetchTeamsLearnersRequest;

      let existingItems: any[] = state.creations.learners.items || [];
      if (page === 0) {
        existingItems = [];
      }

      return {
        ...state,
        creations: {
          ...state.creations,
          learners: {
            ...state.creations.learners,
            loading: true,
            loaded: false,
            error: null,
            items: existingItems,
          },
        },
      };
    }

    case FETCHING_CREATIONS_LEARNERS_REPORT_SUCCEEDED: {
      const { response } = action.payload as FetchTeamsLearnersResponse;
      const existingItems: any[] = state.creations.learners.items || [];
      let records = response.records;

      if (response.currentPage > 0) {
        records = [...existingItems, ...records];
      }

      return {
        ...state,
        creations: {
          ...state.creations,
          learners: {
            ...state.creations.learners,
            loading: false,
            loaded: true,
            items: records,
            pagination: {
              currentPage: response.currentPage,
              totalPages: response.totalPages,
              recordsPerPage: response.recordsPerPage,
              totalRecordsCount: response.totalRecordsCount,
              hasMoreItems: response.currentPage < response.totalPages - 1,
            },
          },
        },
      };
    }

    case FETCHING_CREATIONS_LEARNERS_REPORT_FAILED: {
      const { error } = action.payload as FetchTeamsLearnersErrorResponse;
      return {
        ...state,
        creations: {
          ...state.creations,
          learners: {
            ...state.creations.learners,
            loading: false,
            loaded: false,
            error: error,
          },
        },
      };
    }

    case FETCH_SKILLS_REPORT_REQUEST: {

      return {
        ...state,
        teams: {
          ...state.teams,
          skills: {
            ...state.teams.skills,
            loading: true,
            loaded: false,
            error: null
          },
        },
      };
    }

    case FETCHING_SKILLS_REPORT_SUCCEEDED: {
      const { response } = action.payload as FetchTeamsSkillsResponse;
      // sort by percentage
      response.sort((a, b) => b.percentage - a.percentage);
      
      return {
        ...state,
        teams: {
          ...state.teams,
          skills: {
            ...state.teams.skills,
            loading: false,
            loaded: true,
            items: response,
          },
        },
      };
    }

    case FETCHING_SKILLS_FAILED: {
      const { error } = action.payload as FetchTeamsSkillsErrorResponse;
      return {
        ...state,
        teams: {
          ...state.teams,
          skills: {
            ...state.teams.skills,
            loading: false,
            loaded: false,
            error: error,
          },
        },
      };
    }

    case FETCH_LEARNINGS_SKILLS_REPORT_REQUEST: {

      return {
        ...state,
        learnings: {
          ...state.learnings,
          skills: {
            ...state.learnings.skills,
            loading: true,
            loaded: false,
            error: null
          },
        },
      };
    }

    case FETCHING_LEARNINGS_SKILLS_REPORT_SUCCEEDED: {
      const { response } = action.payload as FetchTeamsSkillsResponse;
      return {
        ...state,
        learnings: {
          ...state.learnings,
          skills: {
            ...state.learnings.skills,
            loading: false,
            loaded: true,
            items: response,
          },
        },
      };
    }

    case FETCHING_LEARNINGS_SKILLS_REPORT_FAILED: {
      const { error } = action.payload as FetchTeamsSkillsErrorResponse;
      return {
        ...state,
        learnings: {
          ...state.learnings,
          skills: {
            ...state.learnings.skills,
            loading: false,
            loaded: false,
            error: error,
          },
        },
      };
    }

    default:
      return state;
  }
}
